import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { WearableConnection } from "@/types/wearable";
import { disconnectPatientWearable, getPatientWearableConnections, syncPatientWearableExports } from "@/api/provider/user/patient/wearable";
export const wearableQueryKeys = {
  all: ["user", "patient", "wearables"] as const,
  patient: (patientId: string) => [...wearableQueryKeys.all, patientId] as const
};

/**
 * Hoisted for the same reason as the portal's: React Query memoizes `select` on
 * the data AND the function identity, so an inline arrow re-runs every render,
 * and `data ?? []` would mint a fresh array each time — giving every consumer a
 * new reference on every render.
 */
const EMPTY_WEARABLE_CONNECTIONS: WearableConnection[] = [];
const selectWearableConnections = (data: WearableConnection[] | null): WearableConnection[] => data ?? EMPTY_WEARABLE_CONNECTIONS;

/**
 * Read once, not polled. The chart cannot start a connection any more — the
 * patient authorises on their own phone through a link — so there is no moment
 * here where a row is known to be seconds away from existing.
 */
export function usePatientWearableConnections(patientId, t0) {
  const $ = _c(8);
  const enabled = t0 === undefined ? true : t0;
  let t1;
  if ($[0] !== patientId) {
    t1 = wearableQueryKeys.patient(patientId);
    $[0] = patientId;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== patientId) {
    t2 = () => getPatientWearableConnections(patientId);
    $[2] = patientId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = enabled && !!patientId;
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3,
      select: selectWearableConnections
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return useQuery(t4);
}
export function useDisconnectPatientWearableMutation(patientId) {
  const $ = _c(8);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== patientId) {
    t0 = connectionId => disconnectPatientWearable(patientId, connectionId);
    $[0] = patientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== patientId || $[3] !== queryClient) {
    t1 = updated => {
      if (!updated) {
        return;
      }
      queryClient.setQueryData(wearableQueryKeys.patient(patientId), existing => existing?.map(connection => connection.id === updated.id ? updated : connection));
    };
    $[2] = patientId;
    $[3] = queryClient;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t0 || $[6] !== t1) {
    t2 = {
      mutationFn: t0,
      onSuccess: t1
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useMutation(t2);
}
export function useSyncPatientWearableExportsMutation(patientId) {
  const $ = _c(8);
  const queryClient = useQueryClient();
  let t0;
  if ($[0] !== patientId) {
    t0 = () => syncPatientWearableExports(patientId);
    $[0] = patientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== patientId || $[3] !== queryClient) {
    t1 = updated => {
      if (!updated) {
        return;
      }
      queryClient.setQueryData(wearableQueryKeys.patient(patientId), () => updated);
    };
    $[2] = patientId;
    $[3] = queryClient;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== t0 || $[6] !== t1) {
    t2 = {
      mutationFn: t0,
      onSuccess: t1
    };
    $[5] = t0;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return useMutation(t2);
}